package com.advaita.cloud.drive.ui.api

import kotlinx.browser.localStorage
import kotlinx.browser.window

private const val TOKEN_KEY = "drive.token"

object AuthApi {
    private val loopGuard = LoginLoopGuard(BrowserSessionStore())

    // ADV-1497: null when sessionStorage itself cannot be reached; SignInReturn then does nothing.
    private val returnStore: KeyValueStore? by lazy { runCatching { BrowserSessionStore() }.getOrNull() }

    /** ADV-1497: save the view (Shared, Shared by me, My access) so sign-in can bring it back. */
    private fun saveReturn() {
        runCatching { SignInReturn.save(returnStore, window.location.pathname, window.location.search) }
    }

    /**
     * ADV-1497: on page load, before anything reads `location.search`. When this is the `/ui/`
     * landing page of a sign-in that saved a view, put the view back in the URL (once).
     */
    fun restoreReturn() {
        val target = runCatching {
            SignInReturn.consume(returnStore, window.location.pathname, window.location.search)
        }.getOrNull() ?: return
        runCatching { window.history.replaceState(null, "", target) }
    }

    fun currentToken(): String? = localStorage.getItem(TOKEN_KEY)?.takeIf { it.isNotBlank() }
    fun clearToken() { localStorage.removeItem(TOKEN_KEY) }

    /** True when automatic re-login was abandoned because it was clearly looping (ADV-443). */
    fun loginLoopDetected(): Boolean = loopGuard.isTripped()

    /** An authenticated request succeeded, so any bounce streak is over. */
    fun authSucceeded() = loopGuard.reset()

    /**
     * Explicit user action (the "Sign in" button). Always allowed and clears the loop state —
     * a person asking to sign in must never be blocked by the guard.
     */
    fun startLogin(): Nothing {
        loopGuard.reset()
        saveReturn()
        window.location.href = "/auth/login"
        throw RedirectingException("redirecting to login")
    }

    /**
     * AUTOMATIC bounce after a 401. Guarded: past the cap we stop redirecting, drop the token
     * and reload so [com.advaita.cloud.drive.ui.App] falls through to LoginGate, which explains
     * the failure instead of spinning. ADV-443.
     *
     * ADV-1155: `App.kt`'s startup sequence runs three authenticated calls per page load, and
     * a dead token 401s all three — `window.location.href = ...` does not stop the coroutine
     * already running. [LoginLoopGuard.recordBounce] is idempotent within a page load, so the
     * 2nd and 3rd bounce cost no budget but still re-issue the navigation. Re-issuing matters:
     * a navigation can fail to commit (the user clicks "Stay" on the uploads-running
     * `beforeunload` prompt), and declining to retry it would strand the user with no way to
     * sign in.
     */
    fun goToLogin(): Nothing = when (loopGuard.recordBounce()) {
        BounceDecision.GIVE_UP -> {
            clearToken()
            window.location.reload()
            throw RedirectingException("login loop detected — not redirecting")
        }
        BounceDecision.REDIRECT -> {
            saveReturn()
            window.location.href = "/auth/login"
            throw RedirectingException("redirecting to login")
        }
    }
}
